import { Component, ElementRef, afterNextRender, computed, inject, output, signal, viewChild } from '@angular/core';
import { Router } from '@angular/router';
import { AuthService } from '../../core/auth/auth.service';
import { OccurrenceService } from '../../core/occurrence/occurrence.service';

interface SearchItem {
  label: string;
  icon: string;
  path: string[];
  /** Linha secundária — usada nos resultados de conteúdo (âmbito, tipo, estado). */
  hint?: string;
}

/**
 * Busca global (command palette). Aberta pela lupa do rodapé (ou Ctrl/⌘+K): filtra os destinos
 * da aplicação e, a partir de dois caracteres, procura também ocorrências por código ou título.
 */
@Component({
  selector: 'app-global-search',
  templateUrl: './global-search.html',
})
export class GlobalSearch {
  private readonly router = inject(Router);
  private readonly auth = inject(AuthService);
  private readonly occurrences = inject(OccurrenceService);

  /** Emitido ao fechar (Esc, clique no backdrop ou seleção). */
  readonly close = output<void>();

  private readonly inputRef = viewChild<ElementRef<HTMLInputElement>>('searchInput');
  private debounce?: ReturnType<typeof setTimeout>;

  /** Espelha o menu lateral — uma paleta que não conhece metade da app é pior do que não a ter. */
  private readonly destinations: SearchItem[] = [
    { label: 'Início', icon: 'fe-home', path: ['dashboard'] },
    { label: 'Ocorrências', icon: 'fe-alert-triangle', path: ['ocorrencias'] },
    { label: 'Nova ocorrência', icon: 'fe-plus-circle', path: ['ocorrencias', 'nova'] },
    { label: 'Estatísticas', icon: 'fe-bar-chart-2', path: ['estatisticas'] },
    { label: 'Clientes', icon: 'fe-users', path: ['clientes'] },
    { label: 'Organizações', icon: 'fe-briefcase', path: ['organizacoes'] },
    { label: 'Configurações', icon: 'fe-settings', path: ['configuracoes'] },
    { label: 'Utilizadores', icon: 'fe-user-check', path: ['configuracoes', 'usuarios'] },
    { label: 'Portal público', icon: 'fe-globe', path: ['configuracoes', 'portal'] },
  ];

  protected readonly query = signal('');
  protected readonly highlighted = signal(0);
  protected readonly searching = signal(false);
  private readonly found = signal<SearchItem[]>([]);

  protected readonly items = computed<SearchItem[]>(() => {
    const term = this.query().trim().toLowerCase();
    const routes = term
      ? this.destinations.filter((item) => item.label.toLowerCase().includes(term))
      : this.destinations;
    return [...routes, ...this.found()];
  });

  constructor() {
    afterNextRender(() => this.inputRef()?.nativeElement.focus());
  }

  protected onQuery(event: Event): void {
    const term = (event.target as HTMLInputElement).value;
    this.query.set(term);
    this.highlighted.set(0);
    this.searchOccurrences(term.trim());
  }

  /** A partir de 2 caracteres procura ocorrências no servidor (código, título ou descrição). */
  private searchOccurrences(term: string): void {
    clearTimeout(this.debounce);
    if (term.length < 2) {
      this.found.set([]);
      this.searching.set(false);
      return;
    }
    this.searching.set(true);
    this.debounce = setTimeout(() => {
      this.occurrences.list({ q: term, year: null, page: 0, size: 5 }).subscribe({
        next: (result) => {
          this.found.set(result.content.map((occurrence) => ({
            label: `${occurrence.code} · ${occurrence.title}`,
            icon: 'fe-alert-triangle',
            path: ['ocorrencias', String(occurrence.id)],
            hint: 'Ocorrência',
          })));
          this.searching.set(false);
        },
        error: () => {
          this.found.set([]);
          this.searching.set(false);
        },
      });
    }, 250);
  }

  protected onKeydown(event: KeyboardEvent): void {
    const items = this.items();
    switch (event.key) {
      case 'Escape':
        this.close.emit();
        break;
      case 'ArrowDown':
        event.preventDefault();
        this.highlighted.update((i) => (items.length ? (i + 1) % items.length : 0));
        break;
      case 'ArrowUp':
        event.preventDefault();
        this.highlighted.update((i) => (items.length ? (i - 1 + items.length) % items.length : 0));
        break;
      case 'Enter': {
        event.preventDefault();
        const item = items[this.highlighted()];
        if (item) this.select(item);
        break;
      }
    }
  }

  protected select(item: SearchItem): void {
    clearTimeout(this.debounce);
    this.router.navigate(['/', this.auth.currentTenant() ?? '', ...item.path]);
    this.close.emit();
  }

  protected setHighlight(index: number): void {
    this.highlighted.set(index);
  }
}
